'use client';

import { c as _c } from "react/compiler-runtime";
import type { Address, ChainId } from '@morpho-org/blue-sdk';
import { ZERO } from '@morpho-org/dapp-core';
import { skipToken, useQuery } from '@tanstack/react-query';
import type { VaultV1Migration } from '@/lib/procedures/vaultV1Migration.contract';
import { isDepositDisabledByWarning } from '@/helpers/warnings';
import { orpc } from '@/lib/orpc/client';
import { buildMigrationKey, getMigrationIndex } from './migrationIndex';
const STALE_TIME_MS = 30 * 60 * 1000;

/**
 * Returns the V2 migration target for a given V1 vault, or null if no mapping
 * exists. Backed by `vaultV1.migrations`, then gated by `vaultV1.migrationEligibility`
 * on the V2 target's deposit-blocking warnings and on the V1 vault's withdrawable
 * assets: migration redeems the depositor's position, so a drained vault would only
 * surface an Upgrade CTA that reverts. Indexer freshness is accepted — the scenario
 * this gates is a vault that has been illiquid for a while, not a same-block drain.
 *
 * Fails closed: while eligibility is unknown (loading or errored) the entry stays
 * hidden. Returning `null` when the mapping request fails is likewise deliberate —
 * the upgrade UI silently degrades, and errors are already logged centrally.
 */
export const useV1ToV2MigrationTarget = (v1VaultAddress, chainId) => {
  const $ = _c(7);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = orpc.vaultV1.migrations.queryOptions({
      staleTime: STALE_TIME_MS
    });
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    data
  } = useQuery(t0);
  let t1;
  let t2;
  let target;
  if ($[1] !== chainId || $[2] !== data || $[3] !== v1VaultAddress) {
    target = !data || !v1VaultAddress || chainId === undefined ? null : getMigrationIndex(data).get(buildMigrationKey(chainId, v1VaultAddress)) ?? null;
    t1 = useQuery;
    t2 = orpc.vaultV1.migrationEligibility.queryOptions({
      input: target ? {
        chainId: target.chainId,
        v1Address: target.v1VaultAddress,
        v2Address: target.v2VaultAddress
      } : skipToken,
      refetchOnWindowFocus: false
    });
    $[1] = chainId;
    $[2] = data;
    $[3] = v1VaultAddress;
    $[4] = t1;
    $[5] = t2;
    $[6] = target;
  } else {
    t1 = $[4];
    t2 = $[5];
    target = $[6];
  }
  const {
    data: eligibility,
    error
  } = t1(t2);
  if (!target || !eligibility || error) {
    return null;
  }
  const warnings = eligibility.warnings.map(_temp);
  if (isDepositDisabledByWarning(warnings)) {
    return null;
  }
  return BigInt(eligibility.withdrawableAssets) > ZERO ? target : null;
};
function _temp(t0) {
  const {
    type,
    level
  } = t0;
  return {
    type,
    level: level.toUpperCase()
  };
}